import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  parseSpreadsheet,
  downloadContactTemplate,
  downloadRows,
} from '../../utils/fileParser';
import { buildRecords, normalizeVarKey } from '../../utils/linkGenerator';
import { fetchExistingIds, fetchLinks, saveLinks } from './api';
import ProjectSettings from './ProjectSettings';

function ProjectWorkspace({ theme, project, onBack, onProjectUpdated, onProjectDeleted }) {
  const [tab, setTab] = useState('upload'); // upload | links | settings
  const [headers, setHeaders] = useState([]);
  const [rows, setRows] = useState([]);
  const [fileName, setFileName] = useState('');
  const [respondentCol, setRespondentCol] = useState('');
  const [selectedVars, setSelectedVars] = useState([]);
  const [batchLabel, setBatchLabel] = useState('');
  const [includeShortLinks, setIncludeShortLinks] = useState(false);
  const [existingIds, setExistingIds] = useState(new Set());
  const [result, setResult] = useState(null);
  const [savedRows, setSavedRows] = useState([]);
  const [batchFilter, setBatchFilter] = useState(''); // '' = all batches
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [linksLoading, setLinksLoading] = useState(false);

  const inputStyle = {
    width: '100%',
    padding: '10px 12px',
    backgroundColor: theme.inputBg,
    border: `1px solid ${theme.inputBorder}`,
    borderRadius: 10,
    color: theme.text,
    fontSize: 14,
    outline: 'none',
    boxSizing: 'border-box',
    fontFamily: 'inherit',
  };

  const panel = {
    backgroundColor: theme.surface,
    border: `1px solid ${theme.border}`,
    borderRadius: theme.radius,
    padding: 24,
    marginBottom: 16,
    boxShadow: theme.shadow,
  };

  const refreshExisting = useCallback(async () => {
    const ids = await fetchExistingIds(project.slug);
    setExistingIds(ids);
  }, [project.slug]);

  useEffect(() => {
    refreshExisting();
  }, [refreshExisting]);

  const loadLinks = useCallback(async () => {
    setLinksLoading(true);
    setError('');
    try {
      const data = await fetchLinks(project.slug);
      setSavedRows(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLinksLoading(false);
    }
  }, [project.slug]);

  useEffect(() => {
    if (tab === 'links') loadLinks();
  }, [tab, loadLinks]);

  const guessRespondentCol = (hdrs) => {
    const aliases = ['respondent_id', 'respondent id', 'respondentid', 'id'];
    return (
      hdrs.find((h) => aliases.includes(normalizeVarKey(h))) || hdrs[0] || ''
    );
  };

  const guessVarCols = (hdrs, respondent) => {
    const preferred = ['name', 'tag', 'phone'];
    const picked = [];
    preferred.forEach((p) => {
      const match = hdrs.find((h) => normalizeVarKey(h) === p && h !== respondent);
      if (match) picked.push(match);
    });
    return picked.length ? picked : hdrs.filter((h) => h !== respondent).slice(0, 3);
  };

  const handleFile = async (file) => {
    if (!file) return;
    setError('');
    setResult(null);
    setBusy(true);
    try {
      const parsed = await parseSpreadsheet(file);
      if (!parsed.rows.length) {
        setError('No data rows found in the file.');
        return;
      }
      setHeaders(parsed.headers);
      setRows(parsed.rows);
      setFileName(file.name);
      const resp = guessRespondentCol(parsed.headers);
      setRespondentCol(resp);
      setSelectedVars(guessVarCols(parsed.headers, resp));
    } catch (err) {
      setError(`Could not read file: ${err.message}`);
    } finally {
      setBusy(false);
    }
  };

  const toggleVar = (header) => {
    setSelectedVars((prev) =>
      prev.includes(header) ? prev.filter((h) => h !== header) : [...prev, header]
    );
    setResult(null);
  };

  const effectiveTemplate = useMemo(
    () => ({
      required: ['respondent_id'],
      uniqueKey: 'respondent_id',
      defaults: { tag: project.defaultTag || 'whatsapp' },
    }),
    [project.defaultTag]
  );

  const handleGenerate = async () => {
    if (!respondentCol) {
      setError('Select which column is the respondent ID.');
      return;
    }
    setError('');
    setBusy(true);
    try {
      const mapping = { respondent_id: respondentCol };
      selectedVars.forEach((col) => {
        if (col && col !== respondentCol) {
          mapping[normalizeVarKey(col)] = col;
        }
      });

      const built = buildRecords({
        rows,
        mapping,
        template: effectiveTemplate,
        existingIds,
      });

      if (built.records.length === 0) {
        setResult({
          created: [],
          stats: {
            created: 0,
            skippedExisting: built.stats.skippedExisting,
            skippedDuplicate: built.stats.skippedDuplicate,
            errored: built.stats.errored,
          },
          errors: built.errors,
        });
        return;
      }

      const records = built.records.map((r) => ({
        ...r,
        variables: {
          tag: project.defaultTag || 'whatsapp',
          ...r.variables,
        },
      }));

      const resp = await saveLinks({
        campaign: project.slug,
        records,
        batchLabel: batchLabel.trim() || undefined,
      });

      setResult({
        created: resp.created,
        stats: {
          created: resp.stats.created,
          skippedExisting: built.stats.skippedExisting + resp.stats.skippedExisting,
          skippedDuplicate: built.stats.skippedDuplicate,
          errored: built.stats.errored,
        },
        errors: built.errors,
      });
      await refreshExisting();
    } catch (err) {
      setError(err.message || 'Failed to generate links');
    } finally {
      setBusy(false);
    }
  };

  const batchOptions = useMemo(() => {
    const labels = new Set();
    let hasUnlabeled = false;
    savedRows.forEach((r) => {
      const label = (r.batch_label || '').trim();
      if (label) labels.add(label);
      else hasUnlabeled = true;
    });
    return {
      labels: Array.from(labels).sort((a, b) => a.localeCompare(b)),
      hasUnlabeled,
    };
  }, [savedRows]);

  const filteredSavedRows = useMemo(() => {
    if (!batchFilter) return savedRows;
    if (batchFilter === '__unlabeled__') {
      return savedRows.filter((r) => !(r.batch_label || '').trim());
    }
    return savedRows.filter((r) => (r.batch_label || '').trim() === batchFilter);
  }, [savedRows, batchFilter]);

  const exportLinks = (format, source) => {
    let data;
    let filenameExtra = '';

    const linkColumns = (r) =>
      includeShortLinks
        ? { survey_link: r.link, short_link: r.short_link || '' }
        : { survey_link: r.link };

    if (source === 'batch') {
      data = (result?.created || []).map((r) => ({
        ...r.row_data,
        ...linkColumns(r),
      }));
    } else {
      data = filteredSavedRows.map((r) => ({
        ...r.row_data,
        ...linkColumns(r),
        batch_label: r.batch_label || '',
        created_at: r.created_at,
      }));
      if (batchFilter === '__unlabeled__') filenameExtra = '_unlabeled';
      else if (batchFilter) {
        filenameExtra = `_${batchFilter.replace(/[^a-zA-Z0-9_-]+/g, '_').slice(0, 40)}`;
      }
    }

    if (!data.length) return;
    const stamp = new Date().toISOString().slice(0, 10);
    downloadRows(data, `${project.slug}_links${filenameExtra}_${stamp}`, format);
  };

  const tabBtn = (id, label) => (
    <button
      type="button"
      onClick={() => setTab(id)}
      style={{
        padding: '10px 16px',
        borderRadius: 999,
        border: `1px solid ${tab === id ? theme.accent : theme.border}`,
        background: tab === id ? 'rgba(232,93,4,0.10)' : 'transparent',
        color: tab === id ? theme.accent : theme.textMuted,
        fontWeight: 600,
        fontSize: 14,
        cursor: 'pointer',
        fontFamily: 'inherit',
      }}
    >
      {label}
    </button>
  );

  return (
    <div>
      <button
        type="button"
        onClick={onBack}
        style={{
          background: 'none',
          border: 'none',
          color: theme.textMuted,
          fontSize: 14,
          cursor: 'pointer',
          padding: 0,
          marginBottom: 16,
          fontFamily: 'inherit',
        }}
      >
        ← All projects
      </button>

      <div style={{ marginBottom: 24 }}>
        <h1
          style={{
            margin: 0,
            fontSize: 28,
            fontWeight: 600,
            letterSpacing: '-0.03em',
            color: theme.text,
          }}
        >
          {project.name}
        </h1>
        <p style={{ margin: '8px 0 0', color: theme.textMuted, fontSize: 14 }}>
          /{project.slug} · tag default: {project.defaultTag || 'whatsapp'} ·{' '}
          {existingIds.size} saved link{existingIds.size === 1 ? '' : 's'}
        </p>
      </div>

      <div style={{ display: 'flex', gap: 10, marginBottom: 20, flexWrap: 'wrap' }}>
        {tabBtn('upload', 'Upload contacts')}
        {tabBtn('links', 'Unique links')}
        {tabBtn('settings', 'Settings')}
      </div>

      {tab !== 'settings' && (
        <div
          style={{
            backgroundColor: 'rgba(234, 179, 8, 0.12)',
            border: '1px solid rgba(234, 179, 8, 0.45)',
            borderRadius: theme.radius,
            padding: 14,
            marginBottom: 16,
            color: theme.mode === 'dark' ? '#FDE68A' : '#854D0E',
            fontSize: 13,
            lineHeight: 1.5,
          }}
        >
          Configure the ElevenLabs agent prompt and dynamic variables in ElevenLabs:{' '}
          <a
            href={`https://elevenlabs.io/app/agents/agents/${encodeURIComponent(project.agentId)}`}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: 'inherit', fontWeight: 700 }}
          >
            open agent {project.agentId}
          </a>
        </div>
      )}

      {error && (
        <div
          style={{
            ...panel,
            borderColor: 'rgba(220,38,38,0.35)',
            backgroundColor: 'rgba(220,38,38,0.06)',
            color: theme.danger,
            fontSize: 14,
          }}
        >
          {error}
        </div>
      )}

      {tab === 'upload' && (
        <>
          <div style={panel}>
            <h2 style={{ margin: '0 0 8px', fontSize: 16, color: theme.text }}>
              1. Download template
            </h2>
            <p style={{ margin: '0 0 16px', color: theme.textMuted, fontSize: 14, lineHeight: 1.5 }}>
              Use these exact column names when possible. <code>respondent_id</code> is required.
              Other columns can be mapped after upload.
            </p>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <button
                type="button"
                onClick={() => downloadContactTemplate('csv')}
                style={{
                  padding: '11px 16px',
                  borderRadius: 10,
                  border: `1px solid ${theme.border}`,
                  background: theme.surfaceMuted,
                  color: theme.text,
                  fontWeight: 600,
                  fontSize: 14,
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                }}
              >
                Download CSV template
              </button>
              <button
                type="button"
                onClick={() => downloadContactTemplate('xlsx')}
                style={{
                  padding: '11px 16px',
                  borderRadius: 10,
                  border: `1px solid ${theme.border}`,
                  background: theme.surfaceMuted,
                  color: theme.text,
                  fontWeight: 600,
                  fontSize: 14,
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                }}
              >
                Download XLSX template
              </button>
            </div>
          </div>

          <div style={panel}>
            <h2 style={{ margin: '0 0 8px', fontSize: 16, color: theme.text }}>
              2. Upload contact list
            </h2>
            <p style={{ margin: '0 0 16px', color: theme.textMuted, fontSize: 14 }}>
              CSV or XLSX. First row must be headers.
            </p>
            <label
              style={{
                display: 'inline-flex',
                padding: '11px 16px',
                borderRadius: 10,
                background: 'rgba(232,93,4,0.10)',
                color: theme.accent,
                border: `1px dashed rgba(232,93,4,0.45)`,
                fontWeight: 600,
                fontSize: 14,
                cursor: 'pointer',
              }}
            >
              {fileName ? 'Choose a different file' : 'Choose CSV / XLSX'}
              <input
                type="file"
                accept=".csv,.xlsx,.xls"
                style={{ display: 'none' }}
                onChange={(e) => handleFile(e.target.files[0])}
              />
            </label>
            {fileName && (
              <p style={{ margin: '12px 0 0', color: theme.textMuted, fontSize: 13 }}>
                Loaded <strong style={{ color: theme.text }}>{fileName}</strong> — {rows.length}{' '}
                rows.
              </p>
            )}
          </div>

          {rows.length > 0 && (
            <div style={panel}>
              <h2 style={{ margin: '0 0 8px', fontSize: 16, color: theme.text }}>
                3. Map columns
              </h2>
              <p style={{ margin: '0 0 16px', color: theme.textMuted, fontSize: 14, lineHeight: 1.5 }}>
                Choose the respondent ID column, then select which other columns should be passed
                into ElevenLabs as dynamic variables (e.g. <code>{'{{name}}'}</code>).
              </p>

              <div style={{ marginBottom: 18, maxWidth: 360 }}>
                <label style={{ display: 'block', fontSize: 13, color: theme.textMuted, marginBottom: 6 }}>
                  Respondent ID column *
                </label>
                <select
                  value={respondentCol}
                  onChange={(e) => {
                    setRespondentCol(e.target.value);
                    setResult(null);
                  }}
                  style={{ ...inputStyle, cursor: 'pointer' }}
                >
                  <option value="">— select —</option>
                  {headers.map((h) => (
                    <option key={h} value={h}>
                      {h}
                    </option>
                  ))}
                </select>
              </div>

              <div style={{ marginBottom: 18 }}>
                <div style={{ fontSize: 13, color: theme.textMuted, marginBottom: 10 }}>
                  Columns to pass to ElevenLabs
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                  {headers
                    .filter((h) => h !== respondentCol)
                    .map((h) => {
                      const on = selectedVars.includes(h);
                      return (
                        <button
                          key={h}
                          type="button"
                          onClick={() => toggleVar(h)}
                          style={{
                            padding: '8px 12px',
                            borderRadius: 999,
                            border: `1px solid ${on ? theme.accent : theme.border}`,
                            background: on ? 'rgba(232,93,4,0.10)' : theme.surfaceMuted,
                            color: on ? theme.accent : theme.text,
                            fontSize: 13,
                            fontWeight: 500,
                            cursor: 'pointer',
                            fontFamily: 'inherit',
                          }}
                        >
                          {h}
                        </button>
                      );
                    })}
                </div>
              </div>

              <div style={{ maxWidth: 360 }}>
                <label style={{ display: 'block', fontSize: 13, color: theme.textMuted, marginBottom: 6 }}>
                  Batch label (optional)
                </label>
                <input
                  value={batchLabel}
                  onChange={(e) => setBatchLabel(e.target.value)}
                  placeholder="e.g. First 200"
                  style={inputStyle}
                />
              </div>

              <div style={{ marginTop: 18 }}>
                <ShortLinkToggle
                  theme={theme}
                  checked={includeShortLinks}
                  onChange={setIncludeShortLinks}
                />
              </div>
            </div>
          )}

          {rows.length > 0 && (
            <div style={{ marginBottom: 20 }}>
              <button
                type="button"
                disabled={!respondentCol || busy}
                onClick={handleGenerate}
                style={{
                  padding: '13px 22px',
                  borderRadius: 10,
                  border: 'none',
                  background:
                    !respondentCol || busy ? theme.surfaceMuted : theme.accent,
                  color: !respondentCol || busy ? theme.textFaint : '#fff',
                  fontWeight: 600,
                  fontSize: 15,
                  cursor: !respondentCol || busy ? 'not-allowed' : 'pointer',
                  fontFamily: 'inherit',
                }}
              >
                {busy ? 'Working...' : 'Generate & save unique links'}
              </button>
            </div>
          )}

          {result && (
            <div style={panel}>
              <h2 style={{ margin: '0 0 16px', fontSize: 16, color: theme.text }}>Results</h2>
              <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', marginBottom: 16 }}>
                <Stat theme={theme} label="Saved (new)" value={result.stats.created} color={theme.success} />
                <Stat theme={theme} label="Already saved" value={result.stats.skippedExisting} />
                <Stat theme={theme} label="Duplicates in file" value={result.stats.skippedDuplicate} />
                <Stat
                  theme={theme}
                  label="Errors"
                  value={result.stats.errored}
                  color={result.stats.errored ? theme.danger : theme.textMuted}
                />
              </div>
              {result.created.length > 0 && result.created[0].short_link && (
                <p
                  style={{
                    margin: '0 0 16px',
                    color: theme.textMuted,
                    fontSize: 13,
                    lineHeight: 1.6,
                  }}
                >
                  Example short link:{' '}
                  <code style={{ color: theme.text, fontFamily: theme.mono }}>
                    {result.created[0].short_link}
                  </code>
                  {includeShortLinks
                    ? ' — downloads include both survey_link and short_link columns.'
                    : ' — tick "Also include shortened links" above to add a short_link column to downloads.'}
                </p>
              )}
              {result.created.length > 0 && (
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                  <button
                    type="button"
                    onClick={() => exportLinks('csv', 'batch')}
                    style={{
                      padding: '11px 16px',
                      borderRadius: 10,
                      border: 'none',
                      background: theme.accent,
                      color: '#fff',
                      fontWeight: 600,
                      fontSize: 14,
                      cursor: 'pointer',
                      fontFamily: 'inherit',
                    }}
                  >
                    Download batch CSV
                  </button>
                  <button
                    type="button"
                    onClick={() => exportLinks('xlsx', 'batch')}
                    style={{
                      padding: '11px 16px',
                      borderRadius: 10,
                      border: `1px solid ${theme.border}`,
                      background: theme.surfaceMuted,
                      color: theme.text,
                      fontWeight: 600,
                      fontSize: 14,
                      cursor: 'pointer',
                      fontFamily: 'inherit',
                    }}
                  >
                    Download batch XLSX
                  </button>
                  <button
                    type="button"
                    onClick={() => setTab('links')}
                    style={{
                      padding: '11px 16px',
                      borderRadius: 10,
                      border: `1px solid ${theme.border}`,
                      background: 'transparent',
                      color: theme.textMuted,
                      fontWeight: 600,
                      fontSize: 14,
                      cursor: 'pointer',
                      fontFamily: 'inherit',
                    }}
                  >
                    View all links
                  </button>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {tab === 'links' && (
        <div style={panel}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 12,
              flexWrap: 'wrap',
              marginBottom: 16,
            }}
          >
            <h2 style={{ margin: 0, fontSize: 16, color: theme.text }}>
              Unique links ({filteredSavedRows.length}
              {batchFilter ? ` of ${savedRows.length}` : ''})
            </h2>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
              <button
                type="button"
                onClick={loadLinks}
                style={{
                  padding: '10px 14px',
                  borderRadius: 10,
                  border: `1px solid ${theme.border}`,
                  background: 'transparent',
                  color: theme.textMuted,
                  fontWeight: 600,
                  fontSize: 13,
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                }}
              >
                Refresh
              </button>
              <button
                type="button"
                disabled={!filteredSavedRows.length}
                onClick={() => exportLinks('csv', 'all')}
                style={{
                  padding: '10px 14px',
                  borderRadius: 10,
                  border: 'none',
                  background: filteredSavedRows.length ? theme.accent : theme.surfaceMuted,
                  color: filteredSavedRows.length ? '#fff' : theme.textFaint,
                  fontWeight: 600,
                  fontSize: 13,
                  cursor: filteredSavedRows.length ? 'pointer' : 'not-allowed',
                  fontFamily: 'inherit',
                }}
              >
                Download CSV
              </button>
              <button
                type="button"
                disabled={!filteredSavedRows.length}
                onClick={() => exportLinks('xlsx', 'all')}
                style={{
                  padding: '10px 14px',
                  borderRadius: 10,
                  border: `1px solid ${theme.border}`,
                  background: theme.surfaceMuted,
                  color: filteredSavedRows.length ? theme.text : theme.textFaint,
                  fontWeight: 600,
                  fontSize: 13,
                  cursor: filteredSavedRows.length ? 'pointer' : 'not-allowed',
                  fontFamily: 'inherit',
                }}
              >
                Download XLSX
              </button>
            </div>
          </div>

          {savedRows.length > 0 && (
            <div style={{ marginBottom: 16 }}>
              <ShortLinkToggle
                theme={theme}
                checked={includeShortLinks}
                onChange={setIncludeShortLinks}
              />
            </div>
          )}

          {savedRows.length > 0 && (
            <div style={{ marginBottom: 16, maxWidth: 360 }}>
              <label
                style={{
                  display: 'block',
                  fontSize: 13,
                  color: theme.textMuted,
                  marginBottom: 6,
                }}
              >
                Filter by batch label
              </label>
              <select
                value={batchFilter}
                onChange={(e) => setBatchFilter(e.target.value)}
                style={{ ...inputStyle, cursor: 'pointer' }}
              >
                <option value="">All batches ({savedRows.length})</option>
                {batchOptions.labels.map((label) => {
                  const count = savedRows.filter(
                    (r) => (r.batch_label || '').trim() === label
                  ).length;
                  return (
                    <option key={label} value={label}>
                      {label} ({count})
                    </option>
                  );
                })}
                {batchOptions.hasUnlabeled && (
                  <option value="__unlabeled__">
                    No batch label (
                    {
                      savedRows.filter((r) => !(r.batch_label || '').trim()).length
                    }
                    )
                  </option>
                )}
              </select>
            </div>
          )}

          {linksLoading ? (
            <p style={{ color: theme.textMuted, margin: 0 }}>Loading...</p>
          ) : savedRows.length === 0 ? (
            <p style={{ color: theme.textMuted, margin: 0 }}>
              No links yet. Upload a contact list to generate them.
            </p>
          ) : filteredSavedRows.length === 0 ? (
            <p style={{ color: theme.textMuted, margin: 0 }}>
              No links match this batch label.
            </p>
          ) : (
            <>
              <div
                style={{
                  overflow: 'auto',
                  maxHeight: 480,
                  border: `1px solid ${theme.border}`,
                  borderRadius: 10,
                }}
              >
                <table
                  style={{
                    borderCollapse: 'collapse',
                    fontSize: 12,
                    fontFamily: theme.mono,
                    whiteSpace: 'nowrap',
                  }}
                >
                  <thead>
                    <tr>
                      {[
                        'respondent_id',
                        'survey_link',
                        ...(includeShortLinks ? ['short_link'] : []),
                        'batch_label',
                        'created_at',
                      ].map((col) => (
                        <th
                          key={col}
                          style={{
                            textAlign: 'left',
                            padding: '10px 12px',
                            color: theme.textMuted,
                            borderBottom: `1px solid ${theme.border}`,
                            fontWeight: 600,
                            position: 'sticky',
                            top: 0,
                            background: theme.surface,
                            zIndex: 1,
                          }}
                        >
                          {col}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredSavedRows.slice(0, 50).map((r, i) => (
                      <tr key={i}>
                        <td style={tdStyle(theme)}>{r.respondent_id}</td>
                        <td style={tdStyle(theme)}>{r.link}</td>
                        {includeShortLinks && (
                          <td style={tdStyle(theme)}>{r.short_link || ''}</td>
                        )}
                        <td style={tdStyle(theme)}>{r.batch_label || ''}</td>
                        <td style={tdStyle(theme)}>
                          {r.created_at ? new Date(r.created_at).toLocaleString() : ''}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {filteredSavedRows.length > 50 && (
                <p style={{ color: theme.textFaint, fontSize: 12, margin: '12px 0 0' }}>
                  Showing first 50 of {filteredSavedRows.length}. Download for the full filtered list.
                </p>
              )}
            </>
          )}
        </div>
      )}

      {tab === 'settings' && (
        <ProjectSettings
          theme={theme}
          project={project}
          onUpdated={onProjectUpdated}
          onDeleted={onProjectDeleted}
        />
      )}
    </div>
  );
}

function tdStyle(theme) {
  return {
    padding: '10px 12px',
    color: theme.text,
    borderBottom: `1px solid ${theme.border}`,
    whiteSpace: 'nowrap',
  };
}

function ShortLinkToggle({ theme, checked, onChange }) {
  return (
    <label
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 10,
        cursor: 'pointer',
        maxWidth: 560,
      }}
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        style={{ marginTop: 3, accentColor: theme.accent, cursor: 'pointer' }}
      />
      <span style={{ fontSize: 14, color: theme.text, lineHeight: 1.5 }}>
        Also include shortened links
        <span style={{ display: 'block', fontSize: 13, color: theme.textMuted }}>
          Adds a <code>short_link</code> column (e.g. <code>/s/Xk3pQ9aB</code>) next to the full{' '}
          <code>survey_link</code>. Short links open the same survey with the same respondent
          ID and variables — handy for WhatsApp messages.
        </span>
      </span>
    </label>
  );
}

function Stat({ theme, label, value, color }) {
  return (
    <div>
      <div style={{ color: color || theme.text, fontSize: 24, fontWeight: 700 }}>{value}</div>
      <div style={{ color: theme.textMuted, fontSize: 12 }}>{label}</div>
    </div>
  );
}

export default ProjectWorkspace;
